/**
 * Budget Templates Module
 * 
 * Defines various budget templates for different life situations
 * and provides utilities for template selection and customization
 */

// Budget template definitions
export const BUDGET_TEMPLATES = {
  YOUNG_PROFESSIONAL: {
    id: 'young_professional',
    name: 'Young Professional',
    description: 'Perfect for recent graduates and entry-level professionals building their financial foundation',
    targetAge: '22-28',
    targetSituation: 'Single, entry-level income, building emergency fund',
    categories: {
      'Housing': { percentage: 30, priority: 1, flexible: false },
      'Transportation': { percentage: 15, priority: 2, flexible: true },
      'Food & Dining': { percentage: 12, priority: 3, flexible: true },
      'Entertainment': { percentage: 8, priority: 4, flexible: true },
      'Personal Care': { percentage: 5, priority: 5, flexible: true },
      'Emergency Fund': { percentage: 10, priority: 1, flexible: false },
      'Savings': { percentage: 10, priority: 2, flexible: true },
      'Debt Payments': { percentage: 5, priority: 1, flexible: false },
      'Miscellaneous': { percentage: 5, priority: 6, flexible: true }
    },
    hcolAdjustments: {
      'Housing': 40,
      'Transportation': 18,
      'Food & Dining': 15,
      'Savings': 5,
      'Emergency Fund': 7
    }
  },

  FAMILY_WITH_CHILDREN: {
    id: 'family_with_children',
    name: 'Family with Children',
    description: 'Designed for families with dependents, balancing current needs with future planning',
    targetAge: '28-45',
    targetSituation: 'Married/single parent, supporting dependents',
    categories: {
      'Housing': { percentage: 35, priority: 1, flexible: false },
      'Transportation': { percentage: 12, priority: 2, flexible: false },
      'Food & Dining': { percentage: 18, priority: 1, flexible: true },
      'Childcare & Education': { percentage: 15, priority: 1, flexible: false },
      'Healthcare': { percentage: 8, priority: 1, flexible: false },
      'Savings': { percentage: 5, priority: 3, flexible: true },
      'Emergency Fund': { percentage: 3, priority: 2, flexible: false },
      'Entertainment': { percentage: 2, priority: 4, flexible: true },
      'Personal Care': { percentage: 2, priority: 5, flexible: true }
    },
    hcolAdjustments: {
      'Housing': 45,
      'Childcare & Education': 20,
      'Transportation': 15,
      'Savings': 2,
      'Entertainment': 1
    }
  },

  PRE_RETIREMENT: {
    id: 'pre_retirement',
    name: 'Pre-Retirement/High Earner',
    description: 'For established professionals maximizing savings and investment opportunities',
    targetAge: '45-60',
    targetSituation: 'High income, focusing on retirement preparation',
    categories: {
      'Housing': { percentage: 25, priority: 2, flexible: true },
      'Transportation': { percentage: 8, priority: 3, flexible: true },
      'Food & Dining': { percentage: 10, priority: 4, flexible: true },
      'Retirement Savings': { percentage: 20, priority: 1, flexible: false },
      'Investment Portfolio': { percentage: 15, priority: 1, flexible: false },
      'Healthcare': { percentage: 6, priority: 2, flexible: false },
      'Travel & Leisure': { percentage: 8, priority: 4, flexible: true },
      'Insurance': { percentage: 4, priority: 2, flexible: false },
      'Miscellaneous': { percentage: 4, priority: 5, flexible: true }
    },
    hcolAdjustments: {
      'Housing': 32,
      'Transportation': 10,
      'Food & Dining': 12,
      'Investment Portfolio': 12,
      'Travel & Leisure': 6
    }
  },

  DEBT_PAYOFF: {
    id: 'debt_payoff',
    name: 'Minimalist/Debt Payoff',
    description: 'Focused on aggressive debt elimination and minimal spending',
    targetAge: 'Any',
    targetSituation: 'High debt, committed to elimination strategy',
    categories: {
      'Housing': { percentage: 25, priority: 1, flexible: false },
      'Transportation': { percentage: 8, priority: 2, flexible: false },
      'Food & Dining': { percentage: 10, priority: 2, flexible: true },
      'Debt Payments': { percentage: 35, priority: 1, flexible: false },
      'Emergency Fund': { percentage: 10, priority: 1, flexible: false },
      'Basic Insurance': { percentage: 4, priority: 2, flexible: false },
      'Personal Care': { percentage: 3, priority: 4, flexible: true },
      'Entertainment': { percentage: 2, priority: 5, flexible: true },
      'Miscellaneous': { percentage: 3, priority: 6, flexible: true }
    },
    hcolAdjustments: {
      'Housing': 35,
      'Transportation': 10,
      'Food & Dining': 12,
      'Debt Payments': 30,
      'Emergency Fund': 8
    }
  },

  FIXED_INCOME: {
    id: 'fixed_income',
    name: 'Fixed Income/Retiree',
    description: 'Conservative approach for retirees and those on fixed incomes',
    targetAge: '60+',
    targetSituation: 'Retired, fixed income, health considerations',
    categories: {
      'Housing': { percentage: 40, priority: 1, flexible: false },
      'Healthcare': { percentage: 15, priority: 1, flexible: false },
      'Food & Dining': { percentage: 15, priority: 1, flexible: true },
      'Transportation': { percentage: 8, priority: 2, flexible: false },
      'Entertainment': { percentage: 7, priority: 3, flexible: true },
      'Utilities & Services': { percentage: 6, priority: 2, flexible: false },
      'Insurance': { percentage: 5, priority: 1, flexible: false },
      'Emergency Fund': { percentage: 2, priority: 2, flexible: false },
      'Miscellaneous': { percentage: 2, priority: 4, flexible: true }
    },
    hcolAdjustments: {
      'Housing': 48,
      'Healthcare': 18,
      'Food & Dining': 18,
      'Transportation': 10,
      'Entertainment': 4
    }
  },

  VARIABLE_INCOME: {
    id: 'variable_income',
    name: 'Variable Income/Freelancer',
    description: 'Designed for irregular income with built-in smoothing and flexibility',
    targetAge: 'Any',
    targetSituation: 'Freelance, contract, commission, or seasonal work',
    categories: {
      'Housing': { percentage: 25, priority: 1, flexible: false },
      'Transportation': { percentage: 10, priority: 2, flexible: false },
      'Food & Dining': { percentage: 12, priority: 2, flexible: true },
      'Income Buffer Fund': { percentage: 20, priority: 1, flexible: false },
      'Emergency Fund': { percentage: 10, priority: 1, flexible: false },
      'Savings': { percentage: 8, priority: 3, flexible: true },
      'Healthcare': { percentage: 8, priority: 1, flexible: false },
      'Business Expenses': { percentage: 4, priority: 2, flexible: true },
      'Miscellaneous': { percentage: 3, priority: 4, flexible: true }
    },
    hcolAdjustments: {
      'Housing': 35,
      'Transportation': 12,
      'Healthcare': 10,
      'Income Buffer Fund': 15,
      'Savings': 6
    },
    specialFeatures: {
      incomeSmoothing: true,
      minimumIncomeCalculation: true,
      flexibleCategories: ['Food & Dining', 'Savings', 'Business Expenses', 'Miscellaneous']
    }
  },

  BLANK_BUDGET: {
    id: 'blank_budget',
    name: 'Blank Budget',
    description: 'Start completely from scratch with no predefined categories or allocations',
    targetAge: 'Any',
    targetSituation: 'Users who want complete control over their budget structure',
    categories: {},
    hcolAdjustments: {},
    specialFeatures: {
      isBlank: true,
      allowsCustomCategories: true
    }
  }
};

// Utility functions

/**
 * Get all available template IDs
 */
export function getTemplateIds() {
  return Object.keys(BUDGET_TEMPLATES);
}

/**
 * Get template by ID
 */
export function getTemplate(templateId) {
  return BUDGET_TEMPLATES[templateId];
}

/**
 * Get template with High Cost of Living adjustments applied
 */
export function getTemplateWithHCOL(templateId, applyHCOL = false) {
  const template = { ...BUDGET_TEMPLATES[templateId] };
  
  if (applyHCOL && template.hcolAdjustments) {
    // Apply HCOL adjustments
    const adjustedCategories = { ...template.categories };
    
    // Apply adjustments
    Object.entries(template.hcolAdjustments).forEach(([category, newPercentage]) => {
      if (adjustedCategories[category]) {
        adjustedCategories[category] = {
          ...adjustedCategories[category],
          percentage: newPercentage
        };
      }
    });
    
    template.categories = adjustedCategories;
    template.hcolApplied = true;
  }
  
  return template;
}

/**
 * Calculate dollar amounts for template categories based on monthly income
 */
export function calculateTemplateAmounts(template, monthlyIncome) {
  const amounts = {};
  let totalPercentage = 0;
  
  Object.entries(template.categories).forEach(([category, config]) => {
    const amount = (monthlyIncome * config.percentage) / 100;
    amounts[category] = {
      percentage: config.percentage,
      amount: amount,
      priority: config.priority,
      flexible: config.flexible
    };
    totalPercentage += config.percentage;
  });
  
  // Add unallocated if template doesn't use 100%
  if (totalPercentage < 100) {
    amounts['Unallocated'] = {
      percentage: 100 - totalPercentage,
      amount: (monthlyIncome * (100 - totalPercentage)) / 100,
      priority: 6,
      flexible: true
    };
  }
  
  return amounts;
}

/**
 * Validate template percentages add up correctly
 */
export function validateTemplate(template) {
  const totalPercentage = Object.values(template.categories)
    .reduce((sum, config) => sum + config.percentage, 0);
  
  return {
    isValid: totalPercentage <= 100,
    totalPercentage,
    difference: 100 - totalPercentage
  };
}

/**
 * Get template recommendations based on user input
 */
export function getTemplateRecommendations(userProfile = {}) {
  const recommendations = [];
  const { age, hasChildren, incomeType, debtLevel, lifeStage } = userProfile;
  
  // Simple recommendation logic
  if (incomeType === 'variable' || incomeType === 'freelance') {
    recommendations.push('variable_income');
  }
  
  if (hasChildren) {
    recommendations.push('family_with_children');
  } else if (age && age < 30) {
    recommendations.push('young_professional');
  } else if (age && age >= 60) {
    recommendations.push('fixed_income');
  } else if (age && age >= 45) {
    recommendations.push('pre_retirement');
  }
  
  if (debtLevel === 'high') {
    recommendations.push('debt_payoff');
  }
  
  // Default to young professional if no clear match
  if (recommendations.length === 0) {
    recommendations.push('young_professional');
  }
  
  return recommendations;
}

/**
 * Create custom template from user allocations
 */
export function createCustomTemplate(name, description, categories, isHCOL = false) {
  return {
    id: 'custom_' + Date.now(),
    name: name || 'Custom Template',
    description: description || 'User-created custom budget template',
    targetAge: 'Custom',
    targetSituation: 'User-defined',
    categories,
    isCustom: true,
    hcolApplied: isHCOL,
    createdAt: new Date()
  };
}

// Export template constants for easy access
export const TEMPLATE_IDS = {
  YOUNG_PROFESSIONAL: 'young_professional',
  FAMILY_WITH_CHILDREN: 'family_with_children',
  PRE_RETIREMENT: 'pre_retirement',
  DEBT_PAYOFF: 'debt_payoff',
  FIXED_INCOME: 'fixed_income',
  VARIABLE_INCOME: 'variable_income',
  BLANK_BUDGET: 'blank_budget'
};